import { useCallback, useEffect, useRef } from "react";

import type { AskMessage } from "@performyard/service-contracts";

import type { SSEChunkEvent, SSEDoneEvent, SSEErrorEvent, SSEStatusEvent } from "../types";

import { getHeaders, getUrl } from "../../../api/services/defs/internal-api/internal-base-request";

import { extractSSEMessages, parseSSEMessage } from "../utils";

import { useAskPerformYardStore } from "../AskPerformYardStore";

// Stream timeout in milliseconds (2 minutes)
const STREAM_TIMEOUT_MS = 120_000;

type SendQueryOptions = {
	query: string;
	messages?: AskMessage[];
};

type UseAskStreamReturn = {
	sendQuery: (options: SendQueryOptions) => Promise<void>;
	abort: () => void;
	isStreaming: boolean;
};

/** Hook to send queries and handle SSE streaming responses. */
export const useAskStream = (): UseAskStreamReturn => {
	const abortControllerRef = useRef<AbortController | null>(null);
	const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const readerRef = useRef<ReadableStreamDefaultReader<Uint8Array> | null>(null);

	// Cleanup on unmount to prevent memory leaks
	useEffect(() => {
		return () => {
			if (abortControllerRef.current) {
				abortControllerRef.current.abort();
				abortControllerRef.current = null;
			}
			if (timeoutRef.current) {
				clearTimeout(timeoutRef.current);
				timeoutRef.current = null;
			}
			readerRef.current?.cancel();
			readerRef.current = null;
		};
	}, []);

	// Subscribe to status for isStreaming check
	const status = useAskPerformYardStore((state) => state.status);

	// Get actions via getState() to avoid stale closures in async operations
	// This is the recommended Zustand pattern for actions used in callbacks
	const getStoreActions = useCallback(() => useAskPerformYardStore.getState(), []);

	const clearTimeoutRef = useCallback(() => {
		if (timeoutRef.current) {
			clearTimeout(timeoutRef.current);
			timeoutRef.current = null;
		}
	}, []);

	const abort = useCallback(() => {
		if (abortControllerRef.current) {
			abortControllerRef.current.abort();
			abortControllerRef.current = null;
		}
		clearTimeoutRef();
		readerRef.current?.cancel();
		readerRef.current = null;
		const { setStatus, setStreamStage } = getStoreActions();
		setStatus("idle");
		setStreamStage(null);
	}, [clearTimeoutRef, getStoreActions]);

	const sendQuery = useCallback(
		async ({ query, messages = [] }: SendQueryOptions): Promise<void> => {
			// Get fresh store state and actions to avoid stale closures
			const {
				sessionId,
				addUserMessage,
				startAssistantMessage,
				appendToAssistantMessage,
				completeAssistantMessage,
				setStatus,
				setStreamStage,
				setError,
			} = getStoreActions();

			// Abort any existing request
			if (abortControllerRef.current) {
				abortControllerRef.current.abort();
			}

			// Create new abort controller
			abortControllerRef.current = new AbortController();

			// Set up request timeout
			clearTimeoutRef();
			timeoutRef.current = setTimeout(() => {
				if (abortControllerRef.current) {
					abortControllerRef.current.abort();
					getStoreActions().setError("Request timed out. Please try again.");
				}
			}, STREAM_TIMEOUT_MS);

			// Add user message to store
			addUserMessage(query);

			// Clear any previous error and set thinking status
			setError(null);
			setStatus("thinking");

			// Track assistant message ID across try/catch so we can
			// complete it as incomplete if an error occurs mid-stream
			let assistantMessageId: string | null = null;

			try {
				const url = getUrl("/v1/ai/ask/stream");
				const headers = getHeaders({ body: JSON.stringify({}) });

				const response = await fetch(url, {
					method: "POST",
					headers: {
						...headers,
						"Content-Type": "application/json",
					} as Record<string, string>,
					body: JSON.stringify({
						query,
						sessionId,
						messages: messages.map((m) => ({
							role: m.role,
							content: m.content,
						})),
					}),
					signal: abortControllerRef.current.signal,
				});

				if (!response.ok) {
					const errorData = await response.json().catch(() => ({}));
					throw new Error(errorData.message || `Request failed: ${response.statusText}`);
				}

				if (!response.body) {
					throw new Error("Response body is null");
				}

				// Start assistant message for streaming
				assistantMessageId = startAssistantMessage();

				// Read the SSE stream
				const reader = response.body.getReader();
				readerRef.current = reader;
				const decoder = new TextDecoder();
				let buffer = "";

				let streamDone = false;
				while (!streamDone) {
					const { done, value } = await reader.read();

					if (done) {
						streamDone = true;
						continue;
					}

					// Decode chunk and add to buffer
					buffer += decoder.decode(value, { stream: true });

					// Extract and process complete SSE messages
					const { messages: sseMessages, remainingBuffer } = extractSSEMessages(buffer);
					buffer = remainingBuffer;

					for (const message of sseMessages) {
						const parsed = parseSSEMessage(message);

						if (!parsed) {
							continue;
						}

						const { eventType, data } = parsed;

						switch (eventType) {
							case "status": {
								const statusEvent = data as SSEStatusEvent;
								setStreamStage(statusEvent.stage);
								break;
							}
							case "chunk": {
								const chunkEvent = data as SSEChunkEvent;
								appendToAssistantMessage(assistantMessageId, chunkEvent.text);
								break;
							}
							case "done": {
								const doneEvent = data as SSEDoneEvent;
								completeAssistantMessage(assistantMessageId, doneEvent.sources, doneEvent.confidence);
								clearTimeoutRef();
								reader.cancel();
								return;
							}
							case "error": {
								const errorEvent = data as SSEErrorEvent;
								setError(errorEvent.message);
								clearTimeoutRef();
								reader.cancel();
								return;
							}
						}
					}
				}

				// Stream ended without a done event — the backend likely crashed
				// mid-stream. Complete the message but mark it as incomplete so
				// the UI can show a subtle indicator to the user.
				console.warn("[AskStream] Stream ended without done event");
				clearTimeoutRef();
				completeAssistantMessage(assistantMessageId, undefined, undefined, true);
			} catch (err) {
				// Don't treat abort as an error
				if (err instanceof Error && err.name === "AbortError") {
					return;
				}

				// If an error occurred while streaming (e.g. lost connection),
				// complete the in-progress message as incomplete so it doesn't
				// show the streaming cursor forever
				if (assistantMessageId) {
					completeAssistantMessage(assistantMessageId, undefined, undefined, true);
				}

				const errorMessage = err instanceof Error ? err.message : "An unexpected error occurred";
				getStoreActions().setError(errorMessage);
			} finally {
				abortControllerRef.current = null;
				clearTimeoutRef();
				readerRef.current?.cancel();
				readerRef.current = null;
			}
		},
		[clearTimeoutRef, getStoreActions]
	);

	return {
		sendQuery,
		abort,
		isStreaming: status === "thinking" || status === "streaming",
	};
};
